import { NgOptimizedImage, TitleCasePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  MatTimepicker,
  MatTimepickerInput,
  MatTimepickerToggle,
} from '@angular/material/timepicker';
import {
  RdsBadgeComponent,
  RdsButtonComponent,
  RdsDateRangeInputComponent,
  RdsDateRangePickerComponent,
  RdsDatepickerToggleComponent,
  RdsEndDateDirective,
  RdsErrorDirective,
  RdsFormFieldComponent,
  RdsIconButtonComponent,
  RdsIconComponent,
  RdsInputDirective,
  RdsLabelDirective,
  RdsSelectComponent,
  RdsSingleSelectOptionComponent,
  RdsStartDateDirective,
  RdsSwitchComponent,
  RdsTextButtonComponent,
  RdsTooltipDirective,
} from '@rds/angular-components';
import { differenceInMinutes } from 'date-fns';
import { addDays } from 'date-fns/addDays';
import { isBefore } from 'date-fns/isBefore';
import { startOfDay } from 'date-fns/startOfDay';

import {
  BookingRequestBaseModel,
  BookingRequestDetailsModel,
  RoomBookingFormService,
  RoomDetailsModel,
  RoomLayoutsModel,
  RoomServicesModel,
  RoomSettingsBlockingTimeModel,
  RoomSettingsDurationRangeModel,
  UserSearchAutocompleteComponent,
  dateClass,
  durationToMinutes,
} from '@bookly/shared';

@Component({
  selector: 'bus-room-booking-form',
  imports: [
    FormsModule,
    ReactiveFormsModule,
    RdsStartDateDirective,
    RdsEndDateDirective,
    RdsDatepickerToggleComponent,
    RdsDateRangeInputComponent,
    RdsDateRangePickerComponent,
    RdsIconComponent,
    NgOptimizedImage,
    MatTimepicker,
    MatTimepickerInput,
    MatTimepickerToggle,
    TitleCasePipe,
    UserSearchAutocompleteComponent,
    RdsIconComponent,
    RdsBadgeComponent,
    RdsFormFieldComponent,
    RdsLabelDirective,
    RdsInputDirective,
    RdsErrorDirective,
    RdsSwitchComponent,
    RdsTooltipDirective,
    RdsSelectComponent,
    RdsSingleSelectOptionComponent,
    RdsButtonComponent,
    RdsIconButtonComponent,
    RdsTextButtonComponent,
  ],
  templateUrl: './room-booking-form.component.html',
  styleUrl: './room-booking-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [RoomBookingFormService],
})
export class RoomBookingFormComponent implements OnInit {
  @Input() public set roomDetails(value: RoomDetailsModel | null) {
    if (value) {
      this.#roomBookingFormService.setRoomDataValidators(value);
      this.#roomBookingFormService.addOneYearConstraint();
      this.availableServices = value.manageSpaceConfiguration?.services || [];
      this.defaultLayoutId = value.manageSpaceConfiguration?.defaultLayoutId;
      this.#roomBookingFormService.setServicesControls(this.availableServices);
      this.maxRoomCapacity.set(value.capacity);
      this.minRoomCapacity.set(value.minCapacity ?? 1);
      this.blockingTimes = value.manageSpaceConfiguration?.blockingTimes || [];
      this.label = value.label;
      this.#roomDetails = value;
      this.freezeDays = value.manageSpaceConfiguration?.freezeDays || 0;
      const durationRange = value.manageSpaceConfiguration?.durationRange;
      if (durationRange) {
        const minDuration =
          durationToMinutes(durationRange.min) > 0
            ? this.durationToString(durationRange.min)
            : null;
        const maxDuration =
          durationToMinutes(durationRange.max) > 0
            ? this.durationToString(durationRange.max)
            : null;
        this.durationTooltipText =
          (minDuration
            ? 'Minimum meeting duration: ' + minDuration + '. '
            : '') +
          (maxDuration ? 'Maximum meeting duration: ' + maxDuration + '.' : '');
      }
      this.generalQuestionsEnabled =
        value.manageSpaceConfiguration?.generalQuestionsEnabled || false;
      const isBefore = this.validateFreezeDays(
        this.#roomBookingFormService.roomBookingForm.controls.startDate.value,
        this.freezeDays
      );
      if (isBefore) {
        this.#roomBookingFormService.roomBookingServicesForm.enable();
        this.#roomBookingFormService.roomBookingForm.controls.roomLayoutUUID.enable();
      } else {
        this.#roomBookingFormService.roomBookingServicesForm.reset();
        this.#roomBookingFormService.roomBookingServicesForm.disable();
        this.#roomBookingFormService.roomBookingForm.controls.roomLayoutUUID.disable();
      }
    }
  }

  @Input() public set roomLayouts(value: RoomLayoutsModel[] | null) {
    if (value && value.length > 0 && this.#roomDetails) {
      const activeLayouts = value.filter(layout => layout.active);
      this.layouts.set(activeLayouts);
      this.#roomBookingFormService.setRoomLayoutsValidators(
        activeLayouts,
        this.defaultLayoutId
      );
    }
  }

  @Input() public set bookingRequest(
    booking: BookingRequestDetailsModel | null
  ) {
    if (booking) {
      this.isEdit = true;
      this.duration = differenceInMinutes(booking.endTime, booking.startTime);
      this.#roomBookingFormService.setFormValues(booking);

      if (this.onBehalfEmails.length > 0) {
        this.bookOnBehalfControl.setValue(true, { emitEvent: false });
      }

      if (booking.layout && booking.layout.layoutSelectedTimeDate) {
        this.layoutSelectedTimeDate.set(booking.layout.layoutSelectedTimeDate);
        const currentLayouts = this.layouts();
        const isLayoutActive = currentLayouts.some(
          layout => layout.layoutDexUUID === booking.roomLayoutUUID
        );
        if (!isLayoutActive) {
          const bookingLayout = booking.layout;
          const updatedLayouts = [
            ...currentLayouts,
            {
              layoutName: bookingLayout.roomLayoutName + ' (INACTIVE)',
              layoutDexUUID: booking.roomLayoutUUID,
              imageUrl: bookingLayout.imageUrl,
              capacity: {
                min: bookingLayout.capacityMin,
                max: bookingLayout.capacityMax,
              },
              setupTime: {
                before: bookingLayout.setupBeforeMinutes,
                after: bookingLayout.setupAfterMinutes,
              },
              active: false,
            } as RoomLayoutsModel,
          ];
          this.layouts.set(updatedLayouts);
        }
      }

      if (this.layouts().length > 0) {
        this.#roomBookingFormService.setRoomLayoutsValidators(
          this.layouts(),
          booking.roomLayoutUUID
        );
      }
      const isBefore = this.validateFreezeDays(
        this.#roomBookingFormService.roomBookingForm.controls.startDate.value,
        this.freezeDays
      );
      if (isBefore) {
        this.#roomBookingFormService.roomBookingServicesForm.enable();
        this.#roomBookingFormService.roomBookingForm.controls.roomLayoutUUID.enable();
      } else {
        this.#roomBookingFormService.roomBookingServicesForm.disable();
        this.#roomBookingFormService.roomBookingForm.controls.roomLayoutUUID.disable();
        this.showFreezeWarning = true;
      }
    }
  }

  @Input() public set startDate(date: Date | null) {
    if (date) {
      this.#roomBookingFormService.setEventDateTime(
        date,
        this.#roomDetails,
        this.duration
      );
      const isBefore = this.validateFreezeDays(
        this.#roomBookingFormService.roomBookingForm.controls.startDate.value,
        this.freezeDays
      );
      if (isBefore) {
        this.#roomBookingFormService.roomBookingServicesForm.enable();
        this.#roomBookingFormService.roomBookingForm.controls.roomLayoutUUID.enable();
        this.#roomBookingFormService.roomBookingForm.controls.startDate.markAsTouched();
      } else {
        this.#roomBookingFormService.roomBookingServicesForm.reset();
        this.#roomBookingFormService.roomBookingServicesForm.disable();
        this.#roomBookingFormService.roomBookingForm.controls.roomLayoutUUID.disable();
      }
    }
  }

  @Output() public bookingRequestFormData =
    new EventEmitter<BookingRequestBaseModel>();
  @Output() public cancelHandler = new EventEmitter<void>();

  readonly #roomBookingFormService = inject(RoomBookingFormService);

  protected readonly bookingDetailsForm =
    this.#roomBookingFormService.roomBookingForm;
  protected readonly roomServicesForm =
    this.#roomBookingFormService.roomBookingServicesForm;
  protected readonly unavailableServices =
    this.#roomBookingFormService.unavailableServices;
  protected readonly bookOnBehalfControl = new FormControl<boolean>(false);
  protected readonly onBehalfEmails =
    this.#roomBookingFormService.onBehalfEmails;
  protected readonly dateClass = dateClass;
  protected readonly minDate: Date = new Date();
  protected readonly layouts = signal<RoomLayoutsModel[]>([]);
  protected readonly hasActiveLayouts = computed(() =>
    this.layouts().some(layout => layout.active)
  );
  protected readonly layoutSelectedTimeDate = signal<string | null>(null);
  protected readonly updatedLayoutIds = computed(() => {
    const selectedTime = this.layoutSelectedTimeDate();
    if (!selectedTime) return new Set<string>();
    const selectedDate = new Date(selectedTime);
    return new Set(
      this.layouts()
        .filter(
          layout =>
            layout.updateTime && new Date(layout.updateTime) > selectedDate
        )
        .map(layout => layout.layoutDexUUID)
    );
  });
  protected readonly maxRoomCapacity = signal<number>(0);
  protected readonly minRoomCapacity = signal<number>(0);
  protected readonly capacityInfo = computed(() => {
    const selectedLayout =
      this.bookingDetailsForm.controls.roomLayoutUUID.value;
    const minCapacity = selectedLayout?.capacity?.min ?? this.minRoomCapacity();
    const maxCapacity = selectedLayout?.capacity?.max ?? this.maxRoomCapacity();
    return `Minimum capacity: ${minCapacity}, Maximum capacity: ${maxCapacity}`;
  });
  protected duration: number | null = null;
  protected durationTooltipText: string | null = null;
  protected label: string = '';
  protected isEdit = false;
  protected availableServices: RoomServicesModel[] = [];
  protected freezeDays = 0;
  protected showFreezeWarning = false;
  protected generalQuestionsEnabled = false;
  protected blockingTimes: RoomSettingsBlockingTimeModel[] = [];
  protected defaultLayoutId: string | undefined = undefined;
  protected yearInAdvance = new Date(
    new Date().setFullYear(new Date().getFullYear() + 1)
  );
  #roomDetails: RoomDetailsModel | null = null;
  readonly #destroyRef = inject(DestroyRef);

  public ngOnInit() {
    this.#roomBookingFormService.layoutsControlValueChange
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe();
    this.bookOnBehalfControl.valueChanges
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(value => {
        if (value) {
          if (this.onBehalfEmails.length === 0) {
            this.#roomBookingFormService.addOnBehalfPerson();
          }
        } else {
          this.#roomBookingFormService.clearOnBehalfPersons();
        }
        this.bookingDetailsForm.updateValueAndValidity();
      });
    this.bookingDetailsForm.controls.startDate.valueChanges
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(date => {
        const isBefore = this.validateFreezeDays(date, this.freezeDays);
        if (isBefore) {
          this.#roomBookingFormService.roomBookingServicesForm.enable();
          this.#roomBookingFormService.roomBookingForm.controls.roomLayoutUUID.enable();
        } else {
          this.#roomBookingFormService.roomBookingServicesForm.reset();
          this.#roomBookingFormService.roomBookingServicesForm.disable();
          this.#roomBookingFormService.roomBookingForm.controls.roomLayoutUUID.disable();
        }
      });
  }

  protected addOnBehalfPerson() {
    this.#roomBookingFormService.addOnBehalfPerson();
  }

  protected removeOnBehalfPerson(index: number) {
    this.#roomBookingFormService.removeOnBehalfPerson(index);
    if (this.onBehalfEmails.length === 0) {
      this.bookOnBehalfControl.setValue(false);
    }
  }

  protected get canAddOnBehalfPerson(): boolean {
    return this.#roomBookingFormService.canAddOnBehalfPerson;
  }

  confirm() {
    this.bookingDetailsForm.markAllAsTouched();
    this.bookingDetailsForm.updateValueAndValidity();
    if (this.bookingDetailsForm.valid) {
      const eventData =
        this.#roomBookingFormService.createRoomBookingRequestBody();
      this.bookingRequestFormData.emit(eventData);
    }
  }

  cancel() {
    this.cancelHandler.emit();
  }

  private validateFreezeDays(selectedDate: Date, freezeDays: number): boolean {
    if (freezeDays === 0) {
      return true;
    }

    const today = new Date();
    const freezeDate = addDays(startOfDay(today), freezeDays);
    const selected = startOfDay(selectedDate);

    return isBefore(freezeDate, selected);
  }

  private durationToString(duration: RoomSettingsDurationRangeModel): string {
    return `${duration.days > 0 ? `${duration.days}d` : ''} ${duration.hours > 0 ? `${duration.hours}h` : ''} ${duration.minutes > 0 ? `${duration.minutes}m` : ''}`;
  }
}
